import { useReactiveVar } from '@apollo/client';
import { isLoggedInVar } from 'graphql/cache';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DeepLinkParamKeys } from 'types/commonTypes';
import { NavigationPath } from 'types/DomainTypes';

type DeepLinkData = {
  lwin18: string | null;
  navTo: string | null;
  action: string | null;
  dataParam: string | null;
};

interface DeepLinkConfig {
  key: string;
  value: string | null;
  action: (data: DeepLinkData) => void;
}

const buildQuery = (params: Partial<DeepLinkData>) => {
  const qs = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value != null) qs.set(key, value);
  });

  return qs.toString();
};

export const useDeepLinks = (disable = false) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLoggedIn = useReactiveVar(isLoggedInVar);
  const { pathname } = useLocation();
  const hasHandledDeepLink = useRef(false);

  const validateParams = (paramsIn: Partial<DeepLinkData>) => {
    return Object.keys(paramsIn).some((key) => paramsIn[key as keyof DeepLinkData]);
  };

  const getDeepLinks = (paramsIn: DeepLinkData): DeepLinkConfig[] => {
    return [
      {
        key: DeepLinkParamKeys.tradeGetProductBylwin18,
        value: paramsIn?.lwin18,
        action: ({ lwin18 }) => {
          navigate(`${NavigationPath.TRADE}?${buildQuery({ lwin18 })}`);
        },
      },
      {
        key: DeepLinkParamKeys.navToParam,
        value: paramsIn?.navTo,
        action: ({ navTo, action, dataParam }) => {
          if (!navTo) return;
          navigate(
            `${navTo}?${buildQuery({
              navTo,
              action,
              dataParam,
            })}`,
          );
        },
      },
      {
        key: DeepLinkParamKeys.actionParam,
        value: paramsIn?.action,
        action: ({ navTo, action, dataParam }) => {
          navigate(
            `${navTo}?${buildQuery({
              navTo,
              action,
              dataParam,
            })}`,
          );
        },
      },

      {
        key: DeepLinkParamKeys.dataParam,
        value: paramsIn?.dataParam,
        action: ({ navTo, action, dataParam }) => {
          navigate(
            `${navTo}?${buildQuery({
              navTo,
              action,
              dataParam,
            })}`,
          );
        },
      },
    ];
  };

  const storeDeepLinks = (deepLinks: DeepLinkConfig[]) => {
    deepLinks.forEach(({ key, value }) => {
      if (value) {
        sessionStorage.setItem(key, value);
      }
    });
  };

  const handleNav = (deepLinks: DeepLinkConfig[]) => {
    const activeLink = deepLinks.find((link) => link.value);

    if (!activeLink) return;

    hasHandledDeepLink.current = true;

    activeLink.action({
      lwin18: sessionStorage.getItem(DeepLinkParamKeys.tradeGetProductBylwin18),
      navTo: sessionStorage.getItem(DeepLinkParamKeys.navToParam),
      action: sessionStorage.getItem(DeepLinkParamKeys.actionParam),
      dataParam: sessionStorage.getItem(DeepLinkParamKeys.dataParam),
    });

    // Cleanup
    deepLinks.forEach(({ key }) => {
      sessionStorage.removeItem(key);
    });
  };

  const getParams = (searchParamsIn: URLSearchParams): DeepLinkData => {
    return {
      lwin18: searchParamsIn.get('lwin18') ?? sessionStorage.getItem(DeepLinkParamKeys.tradeGetProductBylwin18),
      navTo: searchParamsIn.get('navTo') ?? sessionStorage.getItem(DeepLinkParamKeys.navToParam),
      action: searchParamsIn.get('action') ?? sessionStorage.getItem(DeepLinkParamKeys.actionParam),
      dataParam: searchParamsIn.get('dataParam') ?? sessionStorage.getItem(DeepLinkParamKeys.dataParam),
    };
  };

  useEffect(() => {
    if (hasHandledDeepLink.current || disable) return;

    const params = getParams(searchParams);
    const hasValidParams = validateParams(params);

    // console.log('isLogin', {
    //   isLoggedIn,
    //   pathname,
    //   params,
    //   hasValidParams,
    //   hasHandledDeepLink: hasHandledDeepLink.current,
    // });

    if (!hasValidParams) return;

    const deepLinks = getDeepLinks(params);

    storeDeepLinks(deepLinks);

    if (!isLoggedIn) return;

    handleNav(deepLinks);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, searchParams, pathname]);

  return null;
};
